import { useEffect, useState } from "react";
import EventLayout from "@/components/event/EventLayout";
import { Button } from "@/components/ui/button";
import CheckInQrCode from "@/components/event/CheckInQrCode";
import { eventApi } from "@/lib/eventHub";
import { Camera, Upload, Grid3x3, ExternalLink, QrCode as QrIcon } from "lucide-react";

export default function Photos() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => { try { const c = await eventApi.config(); if (m) setConfig(c.data); } catch { /* noop */ } })();
    return () => { m = false; };
  }, []);

  const photos = config?.photos || {};
  const platformName = photos.platform_name || "Kululu";
  const anyUrl = photos.upload_url || photos.gallery_url || photos.live_wall_url;

  const openExternal = (url, kind) => {
    if (!url) return;
    eventApi.track("photo_click", { kind });
    // Prefer opening in a new tab; PWA save-to-home browsers will treat this as an in-app browser view.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-6">
        {/* Check-In QR code — always visible; useful for door signs even before Kululu is live */}
        <div className="mb-8">
          <CheckInQrCode />
        </div>

        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 mb-3">
            <Camera size={12} className="text-fuchsia-700" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-800">Powered by {platformName}</span>
          </div>
          <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight text-emerald-950">Share Your Photos</h1>
          <p className="font-[Outfit] text-sm text-emerald-950/70 mt-2 max-w-xs mx-auto">
            Help us capture the Business Showers experience from every angle.
          </p>
        </div>

        {/* HERO upload CTA · always visible when a URL is configured. Sits above
            the QR so anyone opening this page on a phone can tap-through in
            one motion — no camera-scanning gymnastics required. */}
        {photos.upload_url && (
          <button
            onClick={() => openExternal(photos.upload_url, "upload_hero")}
            data-testid="photos-upload-hero"
            className="w-full mb-6 rounded-3xl bg-gradient-to-br from-fuchsia-600 to-fuchsia-700 hover:from-fuchsia-500 hover:to-fuchsia-600 text-white p-6 shadow-xl shadow-fuchsia-600/25 transition-transform hover:scale-[1.02] active:scale-100 text-left"
          >
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <Upload size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] font-[Outfit] font-bold text-fuchsia-100">Tap to upload</div>
                <div className="font-[Cormorant_Garamond] text-2xl leading-tight">Add your photos &amp; videos</div>
                <div className="text-xs font-[Outfit] text-fuchsia-100 mt-1 flex items-center gap-1">
                  Opens {platformName} · <ExternalLink size={11} />
                </div>
              </div>
            </div>
          </button>
        )}

        {!anyUrl && (
          <div
            data-testid="photos-not-configured"
            className="rounded-2xl border-2 border-dashed border-emerald-950/15 bg-white p-6 text-center mb-6"
          >
            <div className="font-[Cormorant_Garamond] text-xl text-emerald-950">Photos launch on event day</div>
            <div className="text-xs text-emerald-950/60 mt-1 font-[Outfit]">
              Administrators can configure {platformName} links from the Admin dashboard.
            </div>
          </div>
        )}

        <div className="space-y-3">
          {photos.live_wall_url && (
            <Button
              onClick={() => openExternal(photos.live_wall_url, "live_wall")}
              data-testid="photos-wall"
              variant="outline"
              className="w-full h-14 rounded-xl border-2 border-emerald-950 bg-white text-emerald-950 hover:bg-emerald-950 hover:text-white font-[Outfit] font-bold text-base"
            >
              <Grid3x3 size={18} className="mr-2" />
              View Live Photo Wall
            </Button>
          )}
          {(photos.friday_album_url || photos.saturday_album_url) && (
            <div className="grid grid-cols-2 gap-2">
              {photos.friday_album_url && (
                <button
                  onClick={() => openExternal(photos.friday_album_url, "friday_album")}
                  data-testid="photos-friday"
                  className="rounded-xl border-2 border-fuchsia-200 bg-white text-emerald-950 py-3 px-4 text-left hover:border-fuchsia-500 transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700">Friday</div>
                  <div className="font-[Cormorant_Garamond] text-lg leading-tight">VIP Album</div>
                </button>
              )}
              {photos.saturday_album_url && (
                <button
                  onClick={() => openExternal(photos.saturday_album_url, "saturday_album")}
                  data-testid="photos-saturday"
                  className="rounded-xl border-2 border-fuchsia-200 bg-white text-emerald-950 py-3 px-4 text-left hover:border-fuchsia-500 transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700">Saturday</div>
                  <div className="font-[Cormorant_Garamond] text-lg leading-tight">Main Album</div>
                </button>
              )}
            </div>
          )}
        </div>

        {/* QR code */}
        {photos.qr_image_url && (
          <div data-testid="photos-qr" className="mt-6 rounded-2xl border-2 border-emerald-950/10 bg-white p-5 text-center">
            <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800 mb-2">
              <QrIcon size={12} className="inline -mt-0.5 mr-1" /> Scan to upload from another device
            </div>
            <img
              src={photos.qr_image_url}
              alt="Scan to upload"
              className="mx-auto rounded-lg max-w-[220px] w-full"
            />
          </div>
        )}

        {/* Permission notice */}
        {photos.permission_notice && (
          <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-xs font-[Outfit] text-emerald-950/70 leading-relaxed">
            <div className="font-semibold text-emerald-900 mb-1">Photo & Video Permission</div>
            {photos.permission_notice}
          </div>
        )}

        {anyUrl && (
          <p className="mt-6 text-center text-xs font-[Outfit] text-emerald-950/50">
            Uploads open in a new tab · <ExternalLink size={10} className="inline -mt-0.5" />
          </p>
        )}
      </div>
    </EventLayout>
  );
}
